import React, { useState, useEffect } from 'react';
import { api } from '../App';
import Chip from './Chip';
import FormModal from '@shared/ui/FormModal';
import EmptyState from '@shared/ui/EmptyState';
import WorkspacePickerList from './workspace/WorkspacePickerList';

interface Workspace {
  id: string;
  name: string;
  description: string;
  organization_name: string;
  access_level: string;
}

export type TrackableEntityType = 'bill' | 'budget_item';

interface EntityConfig {
  trackingBasePath: string;
  entityParamName: string;
  headerLabel: string;
  trackingLabel: string;
}

const ENTITY_CONFIGS: Record<TrackableEntityType, EntityConfig> = {
  bill: {
    trackingBasePath: '/workspace-bill-tracking',
    entityParamName: 'billId',
    headerLabel: 'Bookmark Bill',
    trackingLabel: 'track this bill',
  },
  budget_item: {
    trackingBasePath: '/workspace-budget-tracking',
    entityParamName: 'budgetItemId',
    headerLabel: 'Track Budget Item',
    trackingLabel: 'track this budget item',
  },
};

interface WorkspaceSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType?: TrackableEntityType;
  entityId: string;
  entityLabel: string;
  onSave: (workspaceIds: string[]) => void;
}

const WorkspaceSelectionModal: React.FC<WorkspaceSelectionModalProps> = ({
  isOpen,
  onClose,
  entityType = 'bill',
  entityId,
  entityLabel,
  onSave,
}) => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);
  const [currentBookmarks, setCurrentBookmarks] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const config = ENTITY_CONFIGS[entityType];

  useEffect(() => {
    if (isOpen) {
      fetchWorkspaces();
      fetchCurrentBookmarks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entityId]);

  const fetchWorkspaces = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${config.trackingBasePath}/workspaces`);
      if (response.data.success) {
        setWorkspaces(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentBookmarks = async () => {
    try {
      const entitySegment = entityType === 'bill' ? 'bill' : 'budget-item';
      const response = await api.get(`${config.trackingBasePath}/${entitySegment}/${entityId}`);
      if (response.data.success) {
        const bookmarkedWorkspaceIds = response.data.data.map((bookmark: any) => bookmark.workspace_id);
        setCurrentBookmarks(bookmarkedWorkspaceIds);
        setSelectedWorkspaces(bookmarkedWorkspaceIds);
      }
    } catch (error) {
      console.error('Error fetching current bookmarks:', error);
    }
  };

  const handleWorkspaceToggle = (workspaceId: string) => {
    setSelectedWorkspaces(prev => 
      prev.includes(workspaceId) 
        ? prev.filter(id => id !== workspaceId)
        : [...prev, workspaceId]
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Determine which workspaces to add and remove
      const toAdd = selectedWorkspaces.filter(id => !currentBookmarks.includes(id));
      const toRemove = currentBookmarks.filter(id => !selectedWorkspaces.includes(id));
      
      const payload = { [config.entityParamName]: entityId };

      if (toAdd.length > 0) {
        await api.post(config.trackingBasePath, {
          ...payload,
          workspaceIds: toAdd
        });
      }
      
      if (toRemove.length > 0) {
        await api.delete(config.trackingBasePath, {
          data: {
            ...payload,
            workspaceIds: toRemove
          }
        });
      }
      
      onSave(selectedWorkspaces);
      onClose();
    } catch (error) {
      console.error('Error saving bookmarks:', error);
      alert('Failed to save bookmarks. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedWorkspaces(currentBookmarks); // Reset to current state
    onClose();
  };

  return (
    <FormModal
      isOpen={isOpen}
      title={`${config.headerLabel} ${entityLabel}`}
      onClose={handleClose}
      onSubmit={handleSave}
      size="md"
      submitLabel="Save bookmarks"
      isSubmitting={saving}
      submitDisabled={loading}
      footerLeft={
        <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
          {selectedWorkspaces.length} workspace{selectedWorkspaces.length !== 1 ? 's' : ''} selected
        </span>
      }
    >
      {!loading && workspaces.length === 0 ? (
        <EmptyState
          title="No eligible workspaces"
          body="You need org_admin or contributor access to bookmark bills."
        />
      ) : (
        <WorkspacePickerList
          workspaces={workspaces.map((w) => ({
            id: w.id,
            name: w.name,
            description: w.description,
            owner_org_name: w.organization_name,
          }))}
          selectedIds={selectedWorkspaces}
          onToggle={handleWorkspaceToggle}
          loading={loading}
          prompt={`Select workspaces to ${config.trackingLabel}:`}
          renderMeta={(workspace) =>
            currentBookmarks.includes(workspace.id) ? (
              <Chip label="Currently tracked" tone="info" size="sm" />
            ) : null
          }
        />
      )}
    </FormModal>
  );
};

export default WorkspaceSelectionModal;
